import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { X, Search } from 'lucide-react';

const PERFIL_LABEL = { 1: 'Chefe', 2: 'Vendedor', 3: 'Gerente Comercial' };

const S = {
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { background: '#0e1420', border: '1px solid #1a2035', borderRadius: 14, padding: 28, width: 500, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
  label: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, display: 'block' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, background: '#0f1117', border: '1px solid #1a2035', color: '#e2e8f0', fontSize: 13, outline: 'none', marginBottom: 16, boxSizing: 'border-box' },
  prazoBox: { background: 'rgba(246,201,14,0.06)', border: '1px solid rgba(246,201,14,0.2)', borderRadius: 10, padding: 16, marginBottom: 16 },
  btn: (c = '#f6c90e') => ({ padding: '10px 18px', borderRadius: 8, border: `1px solid ${c}44`, background: `${c}22`, color: c, fontSize: 13, fontWeight: 700, cursor: 'pointer' }),
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #1a2035', gap: 12 },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const str = typeof iso === 'string' && !iso.includes('T') ? iso + 'T12:00:00' : iso;
    return new Date(str).toLocaleDateString('pt-BR');
  } catch { return '—'; }
};

// Modal reutilizável de "Enviar para Vendedor" — usado em Meus Leads, Kanban e Jornada do Cliente
export default function EnviarVendedorModal({ lead, followups, onClose, onSaved }) {
  const [responsaveis, setResponsaveis] = useState([]);
  const [vendedorId, setVendedorId] = useState(null);
  const [vendedorNome, setVendedorNome] = useState('');
  const [vendedorSearch, setVendedorSearch] = useState('');
  const [showVendDrop, setShowVendDrop] = useState(false);
  const [prazo, setPrazo] = useState('');
  const [notaVendedor, setNotaVendedor] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dropRef = useRef(null);

  useEffect(() => {
    api.get('/responsaveis/').then(r => setResponsaveis(r.data)).catch(() => {});
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowVendDrop(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredResp = responsaveis.filter(u =>
    u.nome.toLowerCase().includes(vendedorSearch.toLowerCase()) ||
    (PERFIL_LABEL[u.perfil_acesso] || '').toLowerCase().includes(vendedorSearch.toLowerCase())
  );

  const save = async () => {
    if (!vendedorId) { setError('Selecione o responsável pelo lead.'); return; }
    if (!prazo) { setError('Informe o prazo para o responsável entrar em contato.'); return; }
    setSaving(true); setError('');
    try {
      await api.patch(`/pre-venda/meus-leads/${lead.id}/`, {
        passar_para_vendedor: true,
        prazo_resposta_vendedor: prazo,
        vendedor_id: vendedorId,
        nota_vendedor: notaVendedor,
      });
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError('Erro: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  return (
    <div style={S.modal} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modalCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f6c90e' }}>Enviar para Vendedor</div>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569' }} onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div style={{ background: '#080c14', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
          {[
            ['Cliente', lead.cliente_nome],
            ['Telefone', lead.cliente_telefone || '—'],
            ['Cidade', lead.cliente_cidade || '—'],
          ].map(([k, v]) => (
            <div key={k} style={S.infoRow}>
              <span style={{ fontSize: 12, color: '#475569' }}>{k}</span>
              <span style={{ fontSize: 13, color: '#cbd5e1' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Follow-ups registrados (opcional) */}
        {followups && followups.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: '#fb923c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Histórico de Follow Ups ({followups.length})
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {followups.map(fu => (
                <div key={fu.id} style={{ background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.18)', borderRadius: 7, padding: '7px 10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: '#fb923c', fontWeight: 600 }}>{fmtDate(fu.data_contato)}</span>
                    <span style={{ fontSize: 10, color: '#475569' }}>{fu.criado_por}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>{fu.nota}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Seleção de responsável */}
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Responsável pelo lead *</label>
          <div ref={dropRef} style={{ position: 'relative' }}>
            {vendedorId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(246,201,14,0.06)', border: '1px solid rgba(246,201,14,0.3)', borderRadius: 8, padding: '10px 12px' }}>
                <span style={{ flex: 1, fontSize: 13, color: '#f6c90e', fontWeight: 600 }}>{vendedorNome}</span>
                <button onClick={() => { setVendedorId(null); setVendedorNome(''); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0f1117', border: '1px solid #1a2035', borderRadius: 8, padding: '10px 12px' }}>
                  <Search size={14} color="#475569" />
                  <input
                    style={{ flex: 1, background: 'none', border: 'none', color: '#e2e8f0', fontSize: 13, outline: 'none' }}
                    placeholder="Buscar vendedor, chefe ou gerente..."
                    value={vendedorSearch}
                    onChange={e => { setVendedorSearch(e.target.value); setShowVendDrop(true); }}
                    onFocus={() => setShowVendDrop(true)}
                    autoFocus
                  />
                </div>
                {showVendDrop && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#0e1420', border: '1px solid #1a2035', borderRadius: 8, zIndex: 200, maxHeight: 180, overflowY: 'auto', marginTop: 2 }}>
                    {filteredResp.length === 0 ? (
                      <div style={{ padding: '10px 14px', fontSize: 13, color: '#475569' }}>Nenhum resultado</div>
                    ) : filteredResp.map(u => (
                      <div key={u.id} onClick={() => { setVendedorId(u.id); setVendedorNome(u.nome); setVendedorSearch(''); setShowVendDrop(false); }}
                        style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #0f1117' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#111827'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <span style={{ fontSize: 13, color: '#e2e8f0' }}>{u.nome}</span>
                        <span style={{ fontSize: 11, color: '#475569' }}>{PERFIL_LABEL[u.perfil_acesso]}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div style={S.prazoBox}>
          <label style={{ ...S.label, color: '#f6c90e' }}>Prazo para entrar em contato *</label>
          <input
            style={{ ...S.input, marginBottom: 12, border: '1px solid rgba(246,201,14,0.3)' }}
            type="datetime-local"
            value={prazo}
            onChange={e => setPrazo(e.target.value)}
          />
          <label style={{ ...S.label, color: '#94a3b8', marginBottom: 5 }}>Nota para o vendedor (opcional)</label>
          <textarea
            style={{ ...S.input, height: 72, resize: 'vertical', marginBottom: 0, border: '1px solid rgba(246,201,14,0.15)' }}
            placeholder="Escreva uma nota para o responsável..."
            value={notaVendedor}
            onChange={e => setNotaVendedor(e.target.value)}
          />
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
            Esta data/hora aparecerá na agenda do responsável selecionado acima.
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, padding: '10px 14px', color: '#f87171', fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
          <button style={S.btn('#475569')} onClick={onClose}>Cancelar</button>
          <button style={S.btn()} onClick={save} disabled={saving}>{saving ? 'Enviando...' : 'Confirmar Envio'}</button>
        </div>
      </div>
    </div>
  );
}
